import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UIBlock } from '@/types/blocks'
import type {
  NodeExecution,
  InterruptEvent,
  EnterpriseStatus,
  OrchestratorSlice,
} from '@/types/orchestrator'
import { initialOrchestratorState, MAX_NODE_HISTORY } from '@/types/orchestrator'
import type { ChatMessage } from '@/types/chat'

// ============================================================================
// Dashboard State Interface
// ============================================================================

interface DashboardState {
  // Stream metadata
  title: string | null
  summary: string | null
  status: 'idle' | 'streaming' | 'completed' | 'failed' | 'partial' | 'paused'
  error: string | null
  traceId: string | null
  durationMs: number | null

  // Block data
  blocks: UIBlock[]

  // Agent selection
  selectedAgentId: string | null

  // Actions
  setTitle: (title: string) => void
  setSummary: (summary: string) => void
  setStatus: (status: DashboardState['status']) => void
  setError: (error: string | null) => void
  setTraceId: (traceId: string) => void
  setDuration: (durationMs: number) => void

  addBlock: (block: UIBlock) => void
  updateBlock: (index: number, block: UIBlock) => void
  removeBlock: (index: number) => void
  clearBlocks: () => void

  selectAgent: (agentId: string) => void

  reset: () => void

  // Chat conversation state
  messages: ChatMessage[]
  currentMessageId: string | null
  addUserMessage: (query: string) => string
  addAssistantMessage: () => string
  finalizeCurrentMessage: () => void
  clearConversation: () => void
  setMessages: (messages: ChatMessage[]) => void
}

// ============================================================================
// Combined State Type (Dashboard + Orchestrator)
// ============================================================================

type CombinedState = DashboardState & OrchestratorSlice

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  title: null,
  summary: null,
  status: 'idle' as const,
  error: null,
  traceId: null,
  durationMs: null,
  blocks: [] as UIBlock[],
  selectedAgentId: null,
  // Chat state
  messages: [] as ChatMessage[],
  currentMessageId: null as string | null,
  // Orchestrator state
  ...initialOrchestratorState,
}

// ============================================================================
// Dashboard Store
// ============================================================================

/** Maximum messages to persist in localStorage to avoid storage bloat. */
const MAX_PERSISTED_MESSAGES = 50

export const useDashboardStore = create<CombinedState>()(
  persist(
    (set) => ({
  ...initialState,

  setTitle: (title) => set({ title }),

  setSummary: (summary) => set({ summary }),

  setStatus: (status) => set({ status }),

  setError: (error) => set({ error }),

  setTraceId: (traceId) => set({ traceId }),

  setDuration: (durationMs) => set({ durationMs }),

  addBlock: (block) =>
    set((state) => ({
      blocks: [...state.blocks, block],
    })),

  updateBlock: (index, block) =>
    set((state) => {
      const blocks = [...state.blocks]
      if (index >= 0 && index < blocks.length) {
        blocks[index] = block
      }
      return { blocks }
    }),

  removeBlock: (index) =>
    set((state) => ({
      blocks: state.blocks.filter((_, i) => i !== index),
    })),

  clearBlocks: () =>
    set({
      title: null,
      summary: null,
      status: 'idle',
      error: null,
      traceId: null,
      durationMs: null,
      blocks: [],
    }),

  selectAgent: (agentId) => set({ selectedAgentId: agentId }),

  reset: () => set(initialState),

  // ============================================================================
  // Chat Conversation Actions
  // ============================================================================

  addUserMessage: (query) => {
    const id = crypto.randomUUID()
    const msg: ChatMessage = {
      id,
      role: 'user',
      timestamp: Date.now(),
      status: 'completed',
      query,
      blocks: [],
      nodeExecutions: [],
    }
    set((state) => ({ messages: [...state.messages, msg] }))
    return id
  },

  addAssistantMessage: () => {
    const id = crypto.randomUUID()
    const msg: ChatMessage = {
      id,
      role: 'assistant',
      timestamp: Date.now(),
      status: 'streaming',
      blocks: [],
      nodeExecutions: [],
    }
    set((state) => ({
      messages: [...state.messages, msg],
      currentMessageId: id,
    }))
    return id
  },

  finalizeCurrentMessage: () => {
    set((state) => {
      if (!state.currentMessageId) return state
      return {
        messages: state.messages.map((msg) =>
          msg.id === state.currentMessageId
            ? {
                ...msg,
                title: state.title,
                summary: state.summary,
                blocks: [...state.blocks],
                nodeExecutions: [...state.nodeExecutions],
                status: (state.status === 'idle' ? 'completed' : state.status) as ChatMessage['status'],
                error: state.error,
                traceId: state.traceId,
                durationMs: state.durationMs,
              }
            : msg
        ),
        currentMessageId: null,
      }
    })
  },

  clearConversation: () =>
    set({
      ...initialState,
      messages: [],
      currentMessageId: null,
    }),

  setMessages: (messages: ChatMessage[]) => set({ messages, currentMessageId: null }),

  // ============================================================================
  // Orchestrator Actions
  // ============================================================================

  setThreadId: (threadId) => set({ threadId }),

  addNodeExecution: (node: NodeExecution) =>
    set((state) => {
      const nodeExecutions = [...state.nodeExecutions, node]
      // Limit to MAX_NODE_HISTORY to prevent memory bloat
      if (nodeExecutions.length > MAX_NODE_HISTORY) {
        nodeExecutions.shift()
      }
      return { nodeExecutions }
    }),

  updateNodeExecution: (nodeId: string, updates: Partial<NodeExecution>) =>
    set((state) => ({
      nodeExecutions: state.nodeExecutions.map((node) =>
        node.nodeId === nodeId ? { ...node, ...updates } : node
      ),
    })),

  clearNodeExecutions: () => set({ nodeExecutions: [] }),

  setCurrentInterrupt: (interrupt: InterruptEvent | null) =>
    set({
      currentInterrupt: interrupt,
      status: interrupt ? 'paused' : undefined,
    }),

  setApprovalPending: (pending: boolean) => set({ isApprovalPending: pending }),

  setEnterpriseStatus: (status: EnterpriseStatus | null) =>
    set({ enterpriseStatus: status }),

  setFallbackMode: (isFallback: boolean, reason?: string) =>
    set({
      isFallbackMode: isFallback,
      fallbackReason: reason ?? null,
    }),

  setOrchestratorMode: (enabled: boolean) => set({ isOrchestratorMode: enabled }),

  resetOrchestratorState: () =>
    set({
      ...initialOrchestratorState,
    }),
    }),
    {
      name: 'uai-dashboard',
      partialize: (state) => ({
        // Only persist conversation messages and agent selection.
        // All transient state (status, blocks, nodeExecutions, etc.) resets on refresh.
        messages: state.messages.slice(-MAX_PERSISTED_MESSAGES),
        selectedAgentId: state.selectedAgentId,
      }),
    },
  ),
)

// ============================================================================
// Selectors (for granular subscriptions)
// ============================================================================

export const selectTitle = (state: CombinedState) => state.title
export const selectSummary = (state: CombinedState) => state.summary
export const selectStatus = (state: CombinedState) => state.status
export const selectError = (state: CombinedState) => state.error
export const selectBlocks = (state: CombinedState) => state.blocks
export const selectBlockCount = (state: CombinedState) => state.blocks.length
export const selectSelectedAgentId = (state: CombinedState) => state.selectedAgentId
export const selectIsStreaming = (state: CombinedState) => state.status === 'streaming'
export const selectIsComplete = (state: CombinedState) =>
  state.status === 'completed' || state.status === 'failed' || state.status === 'partial'

/**
 * Select blocks by type for efficient rendering
 */
export const selectBlocksByType = <T extends UIBlock['type']>(type: T) => {
  return (state: CombinedState) =>
    state.blocks.filter((block): block is Extract<UIBlock, { type: T }> => block.type === type)
}

// ============================================================================
// Orchestrator Selectors
// ============================================================================

export const selectThreadId = (state: CombinedState) => state.threadId
export const selectNodeExecutions = (state: CombinedState) => state.nodeExecutions
export const selectCurrentInterrupt = (state: CombinedState) => state.currentInterrupt
export const selectIsApprovalPending = (state: CombinedState) => state.isApprovalPending
export const selectEnterpriseStatus = (state: CombinedState) => state.enterpriseStatus
export const selectIsFallbackMode = (state: CombinedState) => state.isFallbackMode
export const selectFallbackReason = (state: CombinedState) => state.fallbackReason
export const selectIsOrchestratorMode = (state: CombinedState) => state.isOrchestratorMode
export const selectIsPaused = (state: CombinedState) => state.status === 'paused'

/**
 * Select node executions grouped by phase
 */
export const selectNodesByPhase = (state: CombinedState) => {
  const grouped = {
    orchestration: [] as NodeExecution[],
    delegation: [] as NodeExecution[],
    execution: [] as NodeExecution[],
  }
  for (const node of state.nodeExecutions) {
    grouped[node.phase].push(node)
  }
  return grouped
}

/**
 * Select only running nodes
 */
export const selectRunningNodes = (state: CombinedState) =>
  state.nodeExecutions.filter((node) => node.status === 'running')

// ============================================================================
// Chat Selectors
// ============================================================================

export const selectMessages = (state: CombinedState) => state.messages
export const selectCurrentMessageId = (state: CombinedState) => state.currentMessageId
export const selectSetMessages = (state: CombinedState) => state.setMessages
